import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { kycApi } from '@eshop/shared'
import { useAuthStore } from '../../store/auth'
import type { VendorKYC } from '../../types'
import { Step1Personal } from './steps/Step1Personal'
import { Step2Document } from './steps/Step2Document'
import { Step3Address } from './steps/Step3Address'
import { Step4Business } from './steps/Step4Business'
import { Step5Banking } from './steps/Step5Banking'
import { Step6Terms } from './steps/Step6Terms'

export function KYCWizard() {
  const [currentStep, setCurrentStep] = useState(1)
  const [kyc, setKyc] = useState<VendorKYC | null>(null)
  const [loading, setLoading] = useState(true)
  const [_error, _setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { logout } = useAuthStore()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  useEffect(() => {
    loadKYC()
  }, [])

  const loadKYC = async () => {
    try {
      setLoading(true)
      const data = await kycApi.getMyKYC()
      setKyc(data)
      setCurrentStep(data.current_step)
    } catch (err: any) {
      console.error('Error loading KYC:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStepComplete = (updatedKyc: VendorKYC) => {
    setKyc(updatedKyc)

    // If KYC is submitted, redirect to pending page
    if (updatedKyc.status === 'pending') {
      navigate('/kyc/pending')
    } else {
      setCurrentStep(updatedKyc.current_step)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const steps = [
    { number: 1, title: 'Información Personal', icon: '👤' },
    { number: 2, title: 'Documentación', icon: '📄' },
    { number: 3, title: 'Dirección', icon: '🏠' },
    { number: 4, title: 'Negocio', icon: '🏢' },
    { number: 5, title: 'Cuenta Bancaria', icon: '🏦' },
    { number: 6, title: 'Términos y Condiciones', icon: '✅' },
  ]

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-5xl">
        {/* Logout Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
          >
            <span>🚪</span>
            Cerrar Sesión
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Verificación de Vendedor (KYC)
          </h1>
          <p className="text-sm text-gray-700">
            Complete el proceso de verificación para comenzar a vender en nuestra plataforma
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <div key={step.number} className="flex-1">
                <div className="flex items-center">
                  {/* Step Circle */}
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold
                      ${
                        kyc?.completed_steps.includes(step.number)
                          ? 'bg-green-500 text-white'
                          : step.number === currentStep
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }
                    `}
                  >
                    {kyc?.completed_steps.includes(step.number) ? '✓' : step.icon}
                  </div>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-1 mx-2
                        ${
                          kyc?.completed_steps.includes(step.number)
                            ? 'bg-green-500'
                            : 'bg-gray-200'
                        }
                      `}
                    />
                  )}
                </div>

                {/* Step Title */}
                <p
                  className={`
                    text-xs mt-2 text-center
                    ${
                      step.number === currentStep
                        ? 'text-blue-600 font-semibold'
                        : 'text-gray-500'
                    }
                  `}
                >
                  {step.title}
                </p>
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700">Progreso</span>
              <span className="text-blue-600 font-semibold text-sm">
                {Math.round(kyc?.progress_percentage || 0)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${kyc?.progress_percentage || 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          {_error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{_error}</p>
            </div>
          )}

          {currentStep === 1 && (
            <Step1Personal kyc={kyc} onComplete={handleStepComplete} />
          )}
          {currentStep === 2 && (
            <Step2Document kyc={kyc} onComplete={handleStepComplete} onBack={handleBack} />
          )}
          {currentStep === 3 && (
            <Step3Address kyc={kyc} onComplete={handleStepComplete} onBack={handleBack} />
          )}
          {currentStep === 4 && (
            <Step4Business kyc={kyc} onComplete={handleStepComplete} onBack={handleBack} />
          )}
          {currentStep === 5 && (
            <Step5Banking kyc={kyc} onComplete={handleStepComplete} onBack={handleBack} />
          )}
          {currentStep === 6 && (
            <Step6Terms kyc={kyc} onComplete={handleStepComplete} onBack={handleBack} />
          )}
        </div>

        {/* Help Text */}
        <div className="mt-4 text-center text-sm text-gray-600">
          <p>¿Necesitas ayuda? Contacta a soporte en <a href="mailto:support@eshop.com" className="text-blue-600 hover:underline font-medium">support@eshop.com</a></p>
        </div>
      </div>
    </div>
  )
}
